import React from 'react';
import { motion } from 'framer-motion';
export function FoundersAnatomyOfEndurance() {
  return <div className="min-h-screen bg-gradient-to-b from-black via-[#0a1f2e] to-black" data-id="element-495">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-id="element-496">
        <div className="absolute inset-0" data-id="element-497">
          <img src="https://cdn.magicpatterns.com/uploads/hG6wRmeXbcpGUWshNGVPem/9_Descending_Jacobs_Ladder.jpg" alt="Descending Jacob's Ladder" className="w-full h-full object-cover" data-id="element-498" />
          <div className="absolute inset-0 bg-black/70" data-id="element-499" />
        </div>

        <div className="relative z-10 text-center px-4" data-id="element-500">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-501">
            Founder Story: The Anatomy of Endurance
          </motion.p>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-6xl md:text-8xl font-bold text-white mb-8" data-id="element-502">
            What does it take?
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-2xl text-gray-400 max-w-3xl mx-auto" data-id="element-503">
            To run 240 miles. To build the almost impossible.
          </motion.p>
        </div>
      </section>

      {/* Trait 1: Conviction */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-504">
        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative h-full min-h-[600px]" data-id="element-505">
          <img src="https://cdn.magicpatterns.com/uploads/rmRBKPFnK2E6dvb91taoLe/10_SunsettingCanyon.jpg" alt="Canyon at Sunset" className="w-full h-full object-cover" data-id="element-506" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50" data-id="element-507" />
        </motion.div>

        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="p-8 md:p-16 bg-gradient-to-r from-black/50 to-black" data-id="element-508">
          <div className="max-w-xl" data-id="element-509">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-510">
              Trait 01
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-511">Conviction</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-512">
              <p className="text-xl" data-id="element-513">
                "I am a high conviction leader. I see opportunity in chaos and
                go after seemingly intransigent challenges."
              </p>
              <p data-id="element-514">
                That conviction is encoded in DNA. My grandparents were shipped
                to Siberian labor camps and came to the United States as
                refugees to start over. I come by my relentless drive and
                endurance honestly.
              </p>
              <p data-id="element-515">
                This isn't abstract philosophy. It's lived experience that
                shapes every investment decision, every founder conversation,
                every impossible goal we pursue.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-516">
              <p className="text-white font-semibold mb-3" data-id="element-517">Heritage → Action</p>
              <p className="text-gray-400 text-sm" data-id="element-518">
                From Siberian labor camps to building a $75M global fund
                addressing forced labor risks. The circle completes itself
                through conviction.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Trait 2: Resilience */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-519">
        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="p-8 md:p-16 bg-gradient-to-l from-black/50 to-black order-2 lg:order-1" data-id="element-520">
          <div className="max-w-xl ml-auto" data-id="element-521">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-522">
              Trait 02
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-523">Resilience</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-524">
              <p className="text-xl" data-id="element-525">
                I spent 10 years building and deploying a $75M global fund in
                the face of political headwinds.
              </p>
              <p data-id="element-526">
                Resilience isn't bouncing back. It's moving forward when the
                path disappears. It's conducting risk assessments in Myanmar,
                Indonesia, the Philippines, and India. It's leading
                organizational transformation through CEO transition and
                reduction of force post-Covid.
              </p>
              <p data-id="element-527">
                It's standing at the Moab 240 start line knowing David Goggins—a
                former Navy SEAL—DNF'd this race on his first attempt. And
                finishing anyway. 8th place female.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-528">
              <p className="text-white font-semibold mb-3" data-id="element-529">
                10-Year Track Record
              </p>
              <ul className="space-y-2 text-gray-400 text-sm" data-id="element-530">
                <li data-id="element-531">
                  • $75M raised from family offices, foundations, governments
                </li>
                <li data-id="element-532">• 40 projects deployed globally</li>
                <li data-id="element-533">
                  • Led through political headwinds and organizational
                  transformation
                </li>
                <li data-id="element-534">• Served as Director, Managing Director, and President</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative h-full min-h-[600px] order-1 lg:order-2" data-id="element-535">
          <img src="https://cdn.magicpatterns.com/uploads/jFhgLpSVhVCK824V85YJZE/1_Moab_Start_line.jpg" alt="Moab 240 Start Line" className="w-full h-full object-cover" data-id="element-536" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/50" data-id="element-537" />
        </motion.div>
      </section>

      {/* Trait 3: Execution */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-538">
        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative h-full min-h-[600px]" data-id="element-539">
          <img src="https://cdn.magicpatterns.com/uploads/mworPtjEMYJXiYJ2sRbKWX/2_LeadvilleFinish.jpg" alt="Leadville 100 Finish" className="w-full h-full object-cover" data-id="element-540" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50" data-id="element-541" />

          {/* Image overlay text */}
          <div className="absolute bottom-8 left-8 right-8" data-id="element-542">
            <p className="text-white text-sm mb-1" data-id="element-543">
              Leadville 100 Mile Endurance Run
            </p>
            <p className="text-white/60 text-xs" data-id="element-544">
              Top 30 Female Finisher • 30+ Hour Race
            </p>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="p-8 md:p-16 bg-gradient-to-r from-black/50 to-black" data-id="element-545">
          <div className="max-w-xl" data-id="element-546">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-547">
              Trait 03
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-548">Execution</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-549">
              <p className="text-xl" data-id="element-550">
                Conviction without execution is just noise. Resilience without
                results is just stubbornness.
              </p>
              <p data-id="element-551">
                I successfully completed the Moab 240-miler. Top 30 female
                finisher at Leadville 100. LifeTime Foundation athlete. These
                aren't participation trophies. They're proof of execution under
                extreme conditions.
              </p>
              <p data-id="element-552">
                In fund management: developed criteria, diligence processes, and
                review systems. Deployed $75M across 40 projects. Reported to
                Board of Directors. Engaged auditors. Led organizational change.
                Delivered results.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-553">
              <p className="text-white font-semibold mb-3" data-id="element-554">
                Execution = Results
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-555">
                <div data-id="element-556">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-557">
                    240
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-558">Miles Completed</p>
                </div>
                <div data-id="element-559">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-560">
                    $75M
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-561">Capital Deployed</p>
                </div>
                <div data-id="element-562">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-563">
                    Top 30
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-564">Leadville Finish</p>
                </div>
                <div data-id="element-565">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-566">
                    40
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-567">Projects Funded</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Trait 4: Vision */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-568">
        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="p-8 md:p-16 bg-gradient-to-l from-black/50 to-black order-2 lg:order-1" data-id="element-569">
          <div className="max-w-xl ml-auto" data-id="element-570">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-571">
              Trait 04
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-572">Vision</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-573">
              <p className="text-xl" data-id="element-574">
                Applying an ultrarunner mindset to investing in a world of
                vitality.
              </p>
              <p data-id="element-575">
                Based on the Biophilia Hypothesis and tapping into backgrounds
                in biology, molecular genetics, and corporate sustainability,
                we're building Vitality—a fund that invests in a vibrant planet
                and human health through biomimicry.
              </p>
              <p data-id="element-576">
                This is the long run. Not quarters. Not years. Decades. Building
                a multidisciplinary, unconventional team for challenges that
                don't have obvious solutions yet.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-577">
              <p className="text-white font-semibold mb-3" data-id="element-578">Foundation</p>
              <ul className="space-y-2 text-gray-400 text-sm" data-id="element-579">
                <li data-id="element-580">• Biology & Molecular Genetics background</li>
                <li data-id="element-581">• Executive MBA, Duke University</li>
                <li data-id="element-582">• MPP, University of Chicago</li>
                <li data-id="element-583">• Forte Fellow</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative h-full min-h-[600px] order-1 lg:order-2" data-id="element-584">
          <img src="https://cdn.magicpatterns.com/uploads/hG6wRmeXbcpGUWshNGVPem/9_Descending_Jacobs_Ladder.jpg" alt="Descending Jacob's Ladder" className="w-full h-full object-cover" data-id="element-585" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/50" data-id="element-586" />
        </motion.div>
      </section>

      {/* Closing Statement */}
      <section className="py-32 bg-black" data-id="element-587">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-588">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-589">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-590">
              Natalya Wallin
            </h2>
            <p className="text-xl text-vitalityTeal mb-6" data-id="element-591">
              GP & Managing Director, Vitality VC
            </p>
            <p className="text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto" data-id="element-592">
              Conviction. Resilience. Execution. Vision.
              <br data-id="element-593" />
              <span className="text-white" data-id="element-594">
                These aren't just traits. They're the anatomy of endurance.
              </span>
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}